<script>
   /****************************************************************/
   /* PlotSelectivity                                              */
   /* -----------------------------------------                    */
   /* shows integral selectivity plot for DD-SIMCA results         */
   /****************************************************************/

   import { Vector } from 'mdatools/arrays';
   import { qchisq, pnorm } from 'mdatools/distributions';
   import { Segments, Points } from 'svelte-plots-basic/2d';

   import { csvRow, downloadCSV } from '../utils.js';
   import { LINE_COLORS } from './Defaults.js';


   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';

   let {
      params,                        // JSON with main plot parameters
      props,                         // JSON with main properties (models, results, datasets, etc. formed in AppPlots)
   } = $props()

   // constants
   const xLabel = '1 – selectivity, β';
   const yLabel = 'Expected sensitivity, 1 – ɑ';
   const fileName='plot-selectivity-integral';
   const limX = [-0.05, 1.05];
   const limY = [-0.05, 1.05];


   // get necessary properties
   const res = $derived(props && props.classres ? props.classres : null);
   const simca = $derived(props && props.simca ? props.simca : null);
   const resID = $derived(res ? Object.keys(res)[0] : null);


   // number of points in the ROC curve
   const nPoints = 142; // (1, 1), 50 alphas in [0, 0.1), 40 in [0.1, 0.9), 50 in [0.9, 1.0), (0, 0)

   // method for computing pairs (1 - alpha, beta) and integral selectivity
   function getPlotSeries(getBeta) {

      const x = Vector.zeros(nPoints);
      const y = Vector.zeros(nPoints);

      x.v[0] = 1;
      y.v[0] = 1;

      let i = 1;
      let sel = 0;

      // alpha from 0 to 0.1 (50 points)
      for (let a = 0.001; a < 0.1; a = a + 0.002) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.002
         i = i + 1;
      }

      // alpha from 0.1 to 0.9 (40 points)
      for (let a = 0.1; a < 0.9; a = a + 0.02) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.02
         i = i + 1;
      }

      // alpha from 0.9 to 1.0 (50 points)
      for (let a = 0.9; a < 1.0; a = a + 0.002) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.002
         i = i + 1;
      }

      x.v[i] = 0;
      y.v[i] = 0;

      return {x, y, auc: 1 - sel};
   }

   // this is needed to avoid update of "ra" when alpha changes
   let ra = $state();
   let ncomp = $state();
   let limType = $state();
   $effect(() => {
      if (ncomp !== simca.ncomp || limType !== simca.limType) {
         ncomp = simca.ncomp;
         limType = simca.limType;
         ra = res[resID].v[simca.ncomp - 1];
      }
   })

   // define getBeta function
   const norm1 = $derived(ra ? 1 / (ra.k + ra.s) : null);
   const norm2 = $derived(ra ? 1 / ra.Sz : null);
   function getBeta(a) {
         if (!ra) return null;
         const fcrit = qchisq(1 - a, ra.k);
         const z = fcrit / ra.f0;
         return pnorm((Math.pow(z * norm1, ra.hz) - ra.Mz) * norm2);
   }

   // compute current alpha and corresponding beta
   const curAlpha = $derived(simca.alpha / 100);
   const curBeta = $derived(getBeta(curAlpha));

   // compute new ROC coordinates if getBeta has been changed
   const ps = $derived(getPlotSeries(getBeta));

   // other plot parameters
   let title = $derived(simca.ncomp ? `Selecitivity (A = ${simca.ncomp}, AUC = ${ps.auc.toFixed(4)})` : 'Selectivity');


   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      // 'props.data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(props.data)[0];
      const x = Array.from(ps.x.v);
      const y = Array.from(ps.y.v);

      let out = csvRow([xLabel, yLabel], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([x[i], y[i]], sep, dec);
      }

      downloadCSV(out, fileName);
   }

</script>

{#if ps }

<Axes {title} {xLabel} {yLabel} {limX} {limY} {fileName} {downloadCSVCallback} >

   <Segments xStart={[0]} xEnd={[1]} yStart={[0]} yEnd={[1]} lineColor='#a0a0a0' lineType={3}/>


   <!-- data points with statistics -->
   <PlotSeries
      xValues={ps.x}
      yValues={ps.y}
      plotType='l'
      showLabels={false}
      mainColor={LINE_COLORS['test']}
      lineWidth={1.5}
   />

   <!-- current alpha and beta -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[1 - curAlpha]} yEnd={[1 - curAlpha]} lineColor='#a0a0a0'/>
   <Points xValues={[curBeta]} yValues={[1 - curAlpha]} lineColor={LINE_COLORS['test']} lineWidth={2} faceColor={LINE_COLORS['test']}/>

</Axes>
{/if}
